import React, { createContext, useContext, ReactNode, useState, useCallback, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, IconButton, ButtonProps } from '@mui/material';
import { Close } from '@mui/icons-material';
import { Button } from 'components/index';

interface GetConfirmationProps {
  heading?: string;
  description?: string;
  confirmButton?: Action;
  cancelButton?: Action;
}
interface Action extends ButtonProps {
  label: string;
}

interface PromptContextProps {
  getConfirmation: (props: GetConfirmationProps) => Promise<boolean>;
}

const PromptContext = createContext<PromptContextProps | undefined>(undefined);

interface PromptProviderProps {
  children: ReactNode;
}

const PromptProvider: React.FC<PromptProviderProps> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [heading, setHeading] = useState('');
  const [description, setDescription] = useState('');
  const [actions, setActions] = useState<Action[]>([]);
  const [resolve, setResolve] = useState<(value: boolean) => void>();

  const hidePrompt = useCallback(() => {
    setOpen(false);
  }, []);

  const showPrompt = useCallback((heading: string, description: string, actions: Action[]) => {
    setHeading(heading);
    setDescription(description);
    setActions(actions);
    setOpen(true);
  }, []);

  const getConfirmation = useCallback(
    ({ heading, description, confirmButton, cancelButton }: GetConfirmationProps) => {
      return new Promise<boolean>(resolve => {
        setResolve(() => resolve);

        showPrompt(heading ?? 'Confirm', description ?? 'Are you sure?', [
          {
            ...cancelButton,
            label: cancelButton?.label ?? 'Cancel',
            variant: cancelButton?.variant ?? 'outlined',
            color: cancelButton?.color ?? 'error',
            onClick: () => {
              hidePrompt();
              resolve(false);
            },
          },
          {
            ...confirmButton,
            label: confirmButton?.label ?? 'OK',
            variant: confirmButton?.variant ?? 'contained',
            color: confirmButton?.color ?? 'primary',
            autoFocus: true,
            onClick: () => {
              hidePrompt();
              resolve(true);
            },
          },
        ]);
      });
    },
    [showPrompt],
  );

  const onClose = React.useCallback(() => {
    hidePrompt();
    if (resolve) resolve(false);
  }, [resolve]);

  const contextValue: PromptContextProps = useMemo(() => ({ getConfirmation }), [getConfirmation]);

  return (
    <PromptContext.Provider value={contextValue}>
      {children}
      <Dialog open={open} onClose={onClose} fullWidth aria-labelledby='alert-dialog-title'>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          {heading}
          <IconButton onClick={onClose} sx={{ marginLeft: 'auto' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText dangerouslySetInnerHTML={{ __html: description }} />
        </DialogContent>
        <DialogActions>
          {actions.map((action, index) => (
            <Button key={index} {...action}>
              {action.label}
            </Button>
          ))}
        </DialogActions>
      </Dialog>
    </PromptContext.Provider>
  );
};

const usePrompt = (): PromptContextProps => {
  const context = useContext(PromptContext);
  if (!context) {
    throw new Error('usePrompt must be used within a PromptProvider');
  }
  return context;
};

export { PromptProvider, usePrompt };
